import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { FaCheck, FaDownload, FaRegCopy } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'

import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { isApplyInFlight, type CameraKey } from '@renderer/utils/cameras/cameraPresets'

/**
 * Take one real photo, right here, and look at it.
 *
 * Camera settings are otherwise judged from a live preview and a list of widget
 * values — neither of which is the thing the guest receives. The preview is a
 * low-resolution MJPEG stream that hides exposure and focus problems, and a
 * config tree tells an operator nothing about whether the flash actually fired.
 *
 * So this runs the SAME path as the capture page — `captureImage`, with the
 * booth's own shutter-vs-preview setting — and shows the result. It is
 * deliberately not a `capturePreviewAsBlob()` grab: a shutter-capture booth must
 * be able to test the shutter, which is where the interesting failures live
 * (flash sync, PTP busy, "Perhaps no focus?").
 *
 * Layout note: the shutter sits ON the live preview, the way every phone camera
 * puts it, and the result is a thumbnail you tap to see full size. The first
 * version put a 48px button and two lines of explanation in a row beneath the
 * preview, which spent a lot of vertical space in an already-crowded modal
 * saying things the control's own shape says better.
 */

interface CameraTestCaptureProps {
  cameraKey: CameraKey
  /** Suppressed while a preset apply owns the camera. */
  disabled?: boolean
}

/** Shutter overlaid on the preview, bottom-centre. */
export const CameraTestShutter: React.FC<
  CameraTestCaptureProps & { onShot: (file: File) => void; onError: (message: string) => void }
> = ({ cameraKey, disabled = false, onShot, onError }) => {
  const { t } = useTranslation()
  const [shooting, setShooting] = useState(false)

  const shoot = useCallback(async (): Promise<void> => {
    if (shooting || disabled || isApplyInFlight(cameraKey)) return
    setShooting(true)
    try {
      const settings =
        cameraKey === 'primary'
          ? useSettingsStore.getState().cameraSettings
          : useSettingsStore.getState().secondaryCameraSettings

      // Same arguments the capture pages pass. `usePreview` is the inverse of
      // the operator's shutter-capture choice, so a booth configured to fire the
      // physical shutter tests the physical shutter.
      const file = await unifiedCameraUtils.captureImage(
        cameraKey,
        undefined,
        undefined,
        !settings?.useShutterCapture
      )
      if (!file) {
        onError(t('cameraTest.noFrame', 'The camera returned no photo. Try again.'))
        return
      }
      onShot(file)
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err))
    } finally {
      setShooting(false)
    }
  }, [cameraKey, disabled, onError, onShot, shooting, t])

  return (
    <button
      type="button"
      onClick={() => void shoot()}
      disabled={shooting || disabled}
      aria-label={t('cameraTest.shoot', 'Take a test photo')}
      title={t('cameraTest.shoot', 'Take a test photo')}
      className={`absolute bottom-2 left-1/2 z-10 flex h-9 w-9 -translate-x-1/2 items-center justify-center rounded-full border-2 border-white/80 bg-white/30 backdrop-blur-sm transition ${
        shooting || disabled ? 'opacity-50' : 'hover:bg-white/50 active:scale-90'
      }`}
    >
      <span
        className={`block h-6 w-6 rounded-full ${
          shooting ? 'animate-pulse bg-white/70' : 'bg-white'
        }`}
      />
    </button>
  )
}

/**
 * Whether this renderer can put an image on the clipboard at all. The booth fleet runs a
 * spread of Electron versions, and a button that can only ever fail is worse than no
 * button — so the copy control is simply absent where the API is not.
 */
const canCopyImages =
  typeof ClipboardItem !== 'undefined' && typeof navigator.clipboard?.write === 'function'

/**
 * Re-encode as PNG, because that is the only image type Chromium's async clipboard
 * accepts on write — handing it the camera's JPEG throws `NotAllowedError`.
 */
const toPngBlob = async (source: File): Promise<Blob> => {
  const bitmap = await createImageBitmap(source)
  try {
    const canvas = document.createElement('canvas')
    canvas.width = bitmap.width
    canvas.height = bitmap.height
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('No 2D context for the clipboard copy')
    ctx.drawImage(bitmap, 0, 0)
    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('PNG encode returned nothing'))),
        'image/png'
      )
    })
  } finally {
    bitmap.close()
  }
}

/** Round icon button on the dark zoom backdrop. */
const overlayButtonClass =
  'flex h-10 w-10 items-center justify-center rounded-full border border-white/30 bg-white/10 ' +
  'text-white transition hover:bg-white/25 active:scale-90'

/** `test-photo-2026-09-17-143205.jpg` — sortable, and it never collides with the last one. */
const downloadName = (source: File): string => {
  const pad = (n: number): string => String(n).padStart(2, '0')
  const d = new Date()
  const stamp =
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`
  const byType: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp'
  }
  const dot = source.name.lastIndexOf('.')
  const extension = byType[source.type] ?? (dot > 0 ? source.name.slice(dot + 1) : 'jpg')
  return `test-photo-${stamp}.${extension}`
}

/** The last test shot, as a thumbnail that opens full size. */
export const CameraTestResult: React.FC<{
  file: File | null
  error: string | null
  onClear: () => void
}> = ({ file, error }) => {
  const { t } = useTranslation()
  const [url, setUrl] = useState<string | null>(null)
  const [zoomed, setZoomed] = useState(false)
  const urlRef = useRef<string | null>(null)
  /**
   * What just happened, shown in place of the caption for a moment. Not a toast: this
   * overlay sits above everything, so a toast would land behind the photo.
   */
  const [note, setNote] = useState<{ kind: 'copied' | 'saved' | 'failed'; text: string } | null>(
    null
  )
  const noteTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const flash = useCallback((kind: 'copied' | 'saved' | 'failed', text: string): void => {
    if (noteTimer.current) clearTimeout(noteTimer.current)
    setNote({ kind, text })
    noteTimer.current = setTimeout(() => setNote(null), 2400)
  }, [])

  useEffect(
    () => (): void => {
      if (noteTimer.current) clearTimeout(noteTimer.current)
    },
    []
  )

  const copy = useCallback(async (): Promise<void> => {
    if (!file) return
    try {
      // The PROMISE goes into ClipboardItem, not an awaited blob: Safari — the iPad booth
      // runs the web build — only honours a write built inside the click's user gesture,
      // and awaiting the PNG encode first spends it.
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': toPngBlob(file) })])
      flash('copied', t('cameraTest.copied', 'Photo copied.'))
    } catch (err) {
      console.error('[cameraTest] clipboard copy failed:', err)
      flash('failed', t('cameraTest.copyFailed', 'Could not copy the photo.'))
    }
  }, [file, flash, t])

  const save = useCallback((): void => {
    const href = urlRef.current
    if (!href || !file) return
    // Electron has no save dialog here: the main process' `will-download` handler files it
    // under the booth's downloads folder by date. In a browser the browser takes it.
    const link = document.createElement('a')
    link.href = href
    link.download = downloadName(file)
    document.body.appendChild(link)
    link.click()
    link.remove()
    flash('saved', t('cameraTest.saved', 'Photo saved.'))
  }, [file, flash, t])

  useEffect(() => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current)
    if (!file) {
      urlRef.current = null
      setUrl(null)
      return
    }
    const next = URL.createObjectURL(file)
    urlRef.current = next
    setUrl(next)
  }, [file])

  // Object URLs are a leak if the modal closes mid-life; tie the last one to the
  // component's own lifetime.
  useEffect(
    () => (): void => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current)
      urlRef.current = null
    },
    []
  )

  if (error) {
    return <p className="mt-1 text-center text-[11px] text-red-600">{error}</p>
  }
  if (!url) return null

  return (
    <>
      <button
        type="button"
        onClick={() => setZoomed(true)}
        className="absolute bottom-2 right-2 z-10 overflow-hidden rounded-md border-2 border-white/80 shadow-lg transition hover:scale-105"
        title={t('cameraTest.open', 'Open the test photo')}
      >
        <img
          src={url}
          alt={t('cameraTest.label', 'Test photo')}
          className="h-12 w-12 object-cover"
        />
      </button>

      {zoomed &&
        // PORTALLED TO THE BODY, not rendered in place. z-[300] is meant to put this
        // above the settings modal (z-200), but z-index only ranks siblings inside the
        // same stacking context, and in the Booth Setup panel this lives inside the
        // preview's `sticky top-4` column — and a sticky element ALWAYS starts a stacking
        // context, z-index or not. The overlay was therefore ranked at that column's
        // z-auto, which the settings header (z-10), the camera picker strip (z-20) and
        // the footer buttons all outrank: the photo dimmed the nav rail and then had the
        // sticky chrome punched through the middle of it.
        //
        // From the body there is no ancestor context to be trapped in — and none that can
        // shrink `inset-0` either, which the standalone modal's `container-type: size`
        // shell and, on custom-path booths, ScaleToFit's transform would both do.
        createPortal(
          <div
            className="fixed inset-0 z-[300] flex items-center justify-center bg-black/80 p-6"
            onClick={() => {
              setNote(null)
              setZoomed(false)
            }}
            role="presentation"
          >
            <figure className="flex max-h-full max-w-full flex-col items-center gap-2">
              <img
                src={url}
                alt={t('cameraTest.label', 'Test photo')}
                className="max-h-[80vh] max-w-full rounded-lg object-contain shadow-2xl"
              />
              {/* The backdrop closes on click, so the toolbar keeps its own clicks. */}
              <div
                className="flex items-center gap-2"
                onClick={(e) => e.stopPropagation()}
                role="presentation"
              >
                {canCopyImages && (
                  <button
                    type="button"
                    onClick={() => void copy()}
                    aria-label={t('cameraTest.copy', 'Copy the test photo')}
                    title={t('cameraTest.copy', 'Copy the test photo')}
                    className={overlayButtonClass}
                  >
                    {note?.kind === 'copied' ? (
                      <FaCheck className="h-4 w-4" />
                    ) : (
                      <FaRegCopy className="h-4 w-4" />
                    )}
                  </button>
                )}
                <button
                  type="button"
                  onClick={save}
                  aria-label={t('cameraTest.save', 'Save the test photo')}
                  title={t('cameraTest.save', 'Save the test photo')}
                  className={overlayButtonClass}
                >
                  {note?.kind === 'saved' ? (
                    <FaCheck className="h-4 w-4" />
                  ) : (
                    <FaDownload className="h-4 w-4" />
                  )}
                </button>
              </div>
              <figcaption
                className={`text-center text-xs ${
                  note?.kind === 'failed' ? 'text-red-300' : 'text-white/80'
                }`}
              >
                {note?.text ??
                  t('cameraTest.hint', 'This is what a guest gets. Tap anywhere to close.')}
              </figcaption>
            </figure>
          </div>,
          document.body
        )}
    </>
  )
}
